import React, { useState, useEffect } from "react";
import { useLang } from "../contexts/LangContext";
import { t } from "../i18n/translations";
import { api, formatApiError } from "../lib/api";
import { Gift, Check, CreditCard, Mail, Store, Sparkles, Coins } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://customer-assets-rejwkqb3.emergentagent.net/job_massage-portal-6/artifacts/mj1jkuzz_Gemini_Generated_Image_s48q7ds48q7ds48q.webp";
const FIXED_AMOUNTS = [60, 100, 120, 200];

export default function Vouchers() {
  const { lang } = useLang();
  const [services, setServices] = useState([]);
  const [voucherType, setVoucherType] = useState("amount"); // amount | service
  const [amount, setAmount] = useState(120);
  const [serviceId, setServiceId] = useState("");
  const [isGift, setIsGift] = useState(false);
  const [delivery, setDelivery] = useState("pickup"); // pickup | shipping
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // {voucher_code} for pickup
  const [form, setForm] = useState({
    buyer_name: "", buyer_email: "", buyer_phone: "",
    buyer_address: "", buyer_postal_code: "", buyer_city: "",
    recipient_name: "", recipient_email: "",
    recipient_address: "", recipient_postal_code: "", recipient_city: "",
    message: "",
  });

  useEffect(() => {
    api.get("/services").then((r) => {
      const list = (r.data || []).filter((s) => s.active !== false && Number(s.price_chf) > 0);
      setServices(list);
      if (!serviceId && list.length) setServiceId(list[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedService = services.find((s) => s.id === serviceId);
  const finalAmount = voucherType === "service"
    ? Math.round(Number(selectedService?.price_chf || 0))
    : amount;
  const finalLabel = voucherType === "service"
    ? (selectedService ? pickName(selectedService.name, lang) : t(lang, "vouchers.choose_service"))
    : `CHF ${finalAmount}`;

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validate = () => {
    if (!form.buyer_name.trim()) return "Bitte Name angeben";
    if (!form.buyer_email.trim()) return "Bitte E-Mail angeben";
    if (!form.buyer_phone.trim()) return "Bitte Telefonnummer angeben";
    if (voucherType === "service" && !serviceId) return "Bitte eine Behandlung wählen";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const payload = {
        voucher_type: voucherType,
        amount_chf: voucherType === "amount" ? amount : null,
        service_id: voucherType === "service" ? serviceId : null,
        buyer_name: form.buyer_name.trim(),
        buyer_email: form.buyer_email.trim(),
        buyer_phone: form.buyer_phone.trim(),
        buyer_address: form.buyer_address.trim(),
        buyer_postal_code: form.buyer_postal_code.trim(),
        buyer_city: form.buyer_city.trim(),
        is_gift: isGift,
        recipient_name: isGift ? form.recipient_name.trim() : "",
        recipient_email: isGift ? form.recipient_email.trim() : "",
        recipient_address: isGift ? form.recipient_address.trim() : "",
        recipient_postal_code: isGift ? form.recipient_postal_code.trim() : "",
        recipient_city: isGift ? form.recipient_city.trim() : "",
        message: form.message.trim(),
        delivery_method: delivery,
        language: lang,
        origin_url: window.location.origin,
      };
      const r = await api.post("/vouchers/checkout", payload);
      if (r.data?.redirect && r.data?.url) {
        window.location.href = r.data.url; // → Stripe
        return;
      }
      setSuccess({ voucher_code: r.data?.voucher_code });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Fehler beim Absenden");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-24 sm:py-32 text-center" data-testid="voucher-success">
        <div className="w-16 h-16 rounded-full bg-sage/15 text-sage flex items-center justify-center mx-auto mb-6">
          <Check className="w-7 h-7" strokeWidth={1.5} />
        </div>
        <h1 className="font-display text-4xl text-ink">Vielen Dank für deine Bestellung!</h1>
        <p className="mt-6 text-ink-muted leading-relaxed">
          Wir haben deine Gutschein-Bestellung erhalten.<br />
          Du kannst den Gutschein <strong>vor Ort abholen</strong> und bar oder per Twint bezahlen.<br />
          Wir melden uns, sobald er bereit liegt.
        </p>
        <div className="mt-8 inline-block bg-sand-soft border border-border rounded-md px-6 py-4">
          <div className="text-xs uppercase tracking-widest text-ink-muted">Gutschein-Code</div>
          <div className="font-mono text-lg text-ink mt-1">{success.voucher_code}</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="vouchers-page">
      {/* HERO */}
      <section className="relative h-[42vh] min-h-[320px] overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/55 to-background" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 h-full flex flex-col justify-end pb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-sand mb-4">— {t(lang, "vouchers.subtitle")}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-sand leading-tight max-w-3xl">{t(lang, "vouchers.title")}</h1>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-12 sm:py-16">
        <p className="text-base sm:text-lg text-ink-muted leading-relaxed max-w-3xl">{t(lang, "vouchers.lead")}</p>

        <form onSubmit={submit} className="mt-10 grid lg:grid-cols-12 gap-8 lg:gap-12" data-testid="voucher-form">
          {/* Live preview */}
          <div className="lg:col-span-5 lg:order-2">
            <div className="bg-gradient-to-br from-ink to-ink/85 text-sand rounded-lg p-7 sm:p-8 sticky top-24" data-testid="voucher-preview">
              <Gift className="w-6 h-6 text-terracotta-soft mb-5" strokeWidth={1.5} />
              <div className="text-[10px] uppercase tracking-[0.3em] text-sand/60">Geschenkgutschein</div>
              <div className="font-display text-4xl sm:text-5xl mt-3 break-words">{finalLabel}</div>
              {voucherType === "service" && selectedService && (
                <div className="text-sm text-sand/60 mt-2">Wert: CHF {Math.round(Number(selectedService.price_chf))}.–</div>
              )}
              <div className="mt-6 pt-6 border-t border-sand/15 text-sm text-sand/80 space-y-1.5">
                {isGift && form.recipient_name && <div>Für <span className="text-sand">{form.recipient_name}</span></div>}
                {form.buyer_name && <div className="text-sand/60">von {form.buyer_name}</div>}
                {form.message && <div className="mt-3 italic text-sand/80">«{form.message}»</div>}
              </div>
              <div className="mt-7 text-xs text-sand/50 leading-relaxed">
                {delivery === "shipping" ? "Versand per E-Mail (PDF) · Zahlung online (Karte)" : "Abholung vor Ort · Bar / Twint"}
              </div>
              <div className="mt-2 font-display text-lg text-terracotta-soft">Ayurveda Aargau</div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 space-y-8 lg:order-1">
            {/* 1. Voucher type */}
            <Section title="1. Gutschein-Art">
              <div className="grid grid-cols-2 gap-3">
                <TypeCard
                  active={voucherType === "amount"}
                  onClick={() => setVoucherType("amount")}
                  icon={Coins}
                  title="Geldbetrag"
                  subtitle="Frei verwendbar"
                  testid="voucher-type-amount"
                />
                <TypeCard
                  active={voucherType === "service"}
                  onClick={() => setVoucherType("service")}
                  icon={Sparkles}
                  title="Behandlung"
                  subtitle="Konkrete Massage"
                  testid="voucher-type-service"
                />
              </div>

              {voucherType === "amount" ? (
                <div className="mt-5 grid grid-cols-4 gap-2">
                  {FIXED_AMOUNTS.map((a) => (
                    <button type="button" key={a} onClick={() => setAmount(a)}
                      className={`px-3 py-3 rounded-md border text-sm font-medium transition-colors ${amount === a ? "border-terracotta bg-terracotta/5 text-terracotta" : "border-border bg-white hover:border-ink/30"}`}
                      data-testid={`voucher-amount-${a}`}>
                      CHF {a}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-5">
                  <label className="block">
                    <span className="text-xs uppercase tracking-widest text-ink-muted">Behandlung wählen</span>
                    <select value={serviceId} onChange={(e) => setServiceId(e.target.value)} data-testid="voucher-service-select"
                      className="mt-2 w-full px-4 py-3 border border-border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-terracotta/40">
                      {services.length === 0 && <option value="">…</option>}
                      {services.map((s) => (
                        <option key={s.id} value={s.id}>
                          {String(pickName(s.name, lang))} – CHF {Math.round(Number(s.price_chf))}.–
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
            </Section>

            {/* 2. Buyer details */}
            <Section title="2. Deine Angaben">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Vor- und Nachname" required value={form.buyer_name} onChange={(v) => setField("buyer_name", v)} testid="voucher-buyer-name" />
                <Field label="E-Mail" type="email" required value={form.buyer_email} onChange={(v) => setField("buyer_email", v)} testid="voucher-buyer-email" />
                <Field label="Telefon" required value={form.buyer_phone} onChange={(v) => setField("buyer_phone", v)} testid="voucher-buyer-phone" />
              </div>
            </Section>

            {/* 3. Gift toggle + recipient */}
            <Section title="3. Empfänger">
              <label className="inline-flex items-center gap-3 cursor-pointer select-none" data-testid="voucher-gift-toggle">
                <input type="checkbox" checked={isGift} onChange={(e) => setIsGift(e.target.checked)}
                  className="w-5 h-5 rounded border-border text-terracotta focus:ring-terracotta/40" />
                <span className="text-sm text-ink">Ich möchte den Gutschein an eine andere Person verschenken</span>
              </label>

              {isGift && (
                <div className="mt-5 space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Empfänger Name" value={form.recipient_name} onChange={(v) => setField("recipient_name", v)} testid="voucher-recipient-name" />
                    <Field label="Empfänger E-Mail (optional)" type="email" value={form.recipient_email} onChange={(v) => setField("recipient_email", v)} testid="voucher-recipient-email" />
                  </div>
                </div>
              )}
              <div className="mt-5">
                <Field label="Persönliche Nachricht (erscheint auf dem Gutschein)" textarea value={form.message} onChange={(v) => setField("message", v)} testid="voucher-message" />
              </div>
            </Section>

            {/* 4. Delivery */}
            <Section title="4. Versand">
              <div className="grid sm:grid-cols-2 gap-3">
                <DeliveryCard
                  active={delivery === "pickup"}
                  onClick={() => setDelivery("pickup")}
                  icon={Store}
                  title="Abholung vor Ort"
                  subtitle="Bar oder Twint bei Abholung"
                  testid="voucher-delivery-pickup"
                />
                <DeliveryCard
                  active={delivery === "shipping"}
                  onClick={() => setDelivery("shipping")}
                  icon={Mail}
                  title="Versand per E-Mail (PDF)"
                  subtitle="Direkt in dein Postfach · Zahlung mit Karte"
                  testid="voucher-delivery-shipping"
                />
              </div>
            </Section>

            {/* 5. Payment info */}
            <div className={`rounded-md p-5 border ${delivery === "shipping" ? "bg-terracotta/5 border-terracotta/20" : "bg-sage/10 border-sage/20"}`}>
              <div className="flex items-start gap-3">
                {delivery === "shipping" ? <CreditCard className="w-5 h-5 text-terracotta mt-0.5" strokeWidth={1.5} /> : <Store className="w-5 h-5 text-sage mt-0.5" strokeWidth={1.5} />}
                <div className="text-sm text-ink leading-relaxed">
                  {delivery === "shipping" ? (
                    <>Nach dem Klick auf «Bestellen & bezahlen» wirst du zu unserem sicheren Zahlungspartner <strong>Stripe</strong> weitergeleitet. Bezahlung mit Kreditkarte (Visa/Mastercard). Nach erfolgreicher Zahlung erhältst du den Gutschein <strong>sofort als PDF per E-Mail</strong> – du kannst ihn selbst ausdrucken oder digital weiterleiten.</>
                  ) : (
                    <>Du bezahlst den Gutschein bequem <strong>vor Ort</strong> in der Praxis – bar oder per Twint. Wir melden uns, sobald er bereit liegt.</>
                  )}
                </div>
              </div>
            </div>

            <button type="submit" disabled={submitting}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-terracotta text-white rounded-full hover:bg-terracotta-hover disabled:opacity-30 transition-colors"
              data-testid="voucher-submit">
              {delivery === "shipping" ? <CreditCard className="w-4 h-4" strokeWidth={1.5} /> : <Gift className="w-4 h-4" strokeWidth={1.5} />}
              {submitting ? "Sendet…" : (delivery === "shipping" ? `Bestellen & bezahlen · CHF ${finalAmount}` : "Bestellung absenden")}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <h3 className="font-display text-xl text-ink mb-4">{title}</h3>
    {children}
  </div>
);

const TypeCard = ({ active, onClick, icon: Icon, title, subtitle, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`p-4 rounded-md border text-left transition-colors ${active ? "border-terracotta bg-terracotta/5" : "border-border bg-white hover:border-ink/30"}`}>
    <Icon className={`w-5 h-5 mb-2 ${active ? "text-terracotta" : "text-ink-muted"}`} strokeWidth={1.5} />
    <div className={`text-sm font-medium ${active ? "text-terracotta" : "text-ink"}`}>{title}</div>
    <div className="text-xs text-ink-muted mt-0.5">{subtitle}</div>
  </button>
);

const DeliveryCard = ({ active, onClick, icon: Icon, title, subtitle, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`p-4 rounded-md border text-left transition-colors ${active ? "border-terracotta bg-terracotta/5" : "border-border bg-white hover:border-ink/30"}`}>
    <div className="flex items-start gap-3">
      <Icon className={`w-5 h-5 mt-0.5 ${active ? "text-terracotta" : "text-ink-muted"}`} strokeWidth={1.5} />
      <div>
        <div className={`text-sm font-medium ${active ? "text-terracotta" : "text-ink"}`}>{title}</div>
        <div className="text-xs text-ink-muted mt-0.5">{subtitle}</div>
      </div>
    </div>
  </button>
);

const Field = ({ label, value, onChange, type = "text", textarea = false, testid, required = false }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-widest text-ink-muted">{label}{required && " *"}</span>
    {textarea ? (
      <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={3}
        className="mt-2 w-full px-4 py-2.5 border border-border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-terracotta/40" />
    ) : (
      <input data-testid={testid} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full px-4 py-2.5 border border-border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-terracotta/40" />
    )}
  </label>
);

// Pick the best multilingual name
const pickName = (obj, lang) => (obj && (obj[lang] || obj.de || obj.en)) || "";
